/**
 * LiveInterviewTab — "Live Interview" tab on the Job detail page.
 *
 * Two independent halves, deliberately kept apart:
 *
 *  1. Interview Configuration — the ONE shared, per-job LiveInterviewConfig
 *     (JD context + skills/rubrics/question-mix) every candidate's session
 *     draws its questions from. It needs no session and creates none, and it
 *     never generates a question: it is the Live Interview twin of
 *     ProctoredInterviewTab's ConfigWizard, and uses the same structured
 *     editor shape (skill name + rubric criteria rows + a question-type ×
 *     count grid built from the shared /question-types reference list).
 *     Hydrates from GET /live-interview/jobs/{jobId}/config, which returns
 *     the existing config or null WITHOUT creating one or re-running the
 *     (slow, paid) AI extraction — so reopening this tab is free.
 *
 *  2. Candidate rows — per-submission actions. Real status (Join Now vs.
 *     Completed) for every row is seeded up front from
 *     GET /live-interview/jobs/{jobId}/sessions (listSessionsForJob) rather
 *     than only being learned lazily as each row is acted on — that used to
 *     be the only way a row's status was ever populated, which is why a
 *     completed interview kept showing "Join Now" until its own Join Now/
 *     Copy Candidate URL happened to be clicked. "Join Now" and "Copy
 *     Candidate URL" each independently get-or-create that candidate's
 *     submission-scoped session (getOrCreateSession is idempotent, so both
 *     resolve to the same session) and refresh that row's entry, and each
 *     tracks its OWN pending row: they are independent actions, so a
 *     spinner on one must never disable the other.
 *
 * What this tab deliberately does NOT do: generate the interview's first
 * question. A session is created with no questions at all; the recruiter
 * room's own "Start Interview" button is what triggers first-question
 * generation, at the moment the interview actually starts. Pre-generating it
 * from here left a "pending" question sitting in a room nobody had joined,
 * making the interview look like it had started itself.
 */

import React, { useEffect, useRef, useState } from 'react'
import { toast } from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  VideoCameraIcon, ClipboardDocumentIcon, DocumentTextIcon, SparklesIcon, PlusIcon, TrashIcon,
  CodeBracketIcon, Square3Stack3DIcon, MicrophoneIcon, MinusIcon, XMarkIcon, EnvelopeIcon,
  CheckCircleIcon, PencilSquareIcon, ArrowPathIcon, ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline'

import LoadingSpinner from '@/components/common/LoadingSpinner'
import InfoTooltip from '@/components/common/InfoTooltip'
import { usePermission } from '@/hooks/usePermission'
import { matchingApi } from '@/services/api'
import { fetchQuestionTypes, type QuestionType } from '@/services/questionTypeApi'
import {
  getOrCreateSession, listSessionsForJob, sendCandidateEmail, getConfigForJob, extractJdContext,
  saveSkillsRubricsForJob, getAuditLog,
  type LiveInterviewSession, type LiveInterviewConfig, type SkillRubric, type QuestionMixEntry,
  type AuditLogEntry,
} from '@/services/liveInterviewApi'

// scheduled | in_progress | completed | aborted
const STATUS_LABELS: Record<string, string> = {
  scheduled: 'Not Started',
  in_progress: 'In Progress',
  completed: 'Completed',
  aborted: 'Aborted',
}

const STATUS_BADGE_CLASSES: Record<string, string> = {
  scheduled: 'bg-gray-100 text-gray-700',
  in_progress: 'bg-blue-50 text-blue-700',
  completed: 'bg-green-50 text-green-700',
  aborted: 'bg-red-50 text-red-700',
}

// Same cap Proctored Interview's config enforces, and the same cap the
// backend's own JD-context seeding applies (MAX_SEEDED_SKILLS).
const MAX_SKILLS = 6

function statusLabel(status: string): string {
  return STATUS_LABELS[status] || status
}

function statusBadgeClass(status: string): string {
  return STATUS_BADGE_CLASSES[status] || STATUS_BADGE_CLASSES.scheduled
}

interface LiveInterviewTabProps {
  jobId: string
  isActive?: boolean
}

interface CandidateRow {
  id: string // job_match_id
  candidate_name: string
  candidate_email?: string
  match_score?: number
}

export default function LiveInterviewTab({ jobId, isActive }: LiveInterviewTabProps) {
  const navigate = useNavigate()
  // Added 2026-10-07 — gates Join Now (when it would create/start a
  // session), the Human/AI mode select, Send/Resend Email, and Copy
  // Candidate URL (unlike ProctoredInterviewTab's Copy Link, this one calls
  // getOrCreateSession server-side, so it's a write too, not a pure clipboard
  // copy). Once a session is completed, the same Join Now button becomes a
  // pure "View Scorecard" read — that one stays available to view-only
  // roles. InterviewConfigEditor below (the config/wizard half of this tab)
  // gates itself the same way, via its own usePermission() call.
  const { can } = usePermission()
  const canManage = can('liveInterview.manage')
  // Sessions known so far, keyed by job_match_id — seeded in bulk from
  // listSessionsForJob (below) so every row shows its real status up front,
  // and kept up to date as rows are acted on (getOrCreateSession being
  // idempotent makes that safe): resolveSession's own setSessions call
  // always wins over a stale bulk-fetch value for the same key.
  const [sessions, setSessions] = useState<Record<string, LiveInterviewSession>>({})

  // Whether a live interview config exists for this job — drives the
  // conditional visibility of Join Now / Send Email / Copy Candidate URL.
  // Fetched here (same call InterviewConfigEditor makes, so it hits the React
  // Query cache) so we don't need to thread a prop from inside the editor.
  const { data: config } = useQuery({
    queryKey: ['live-interview-config', jobId],
    queryFn: () => getConfigForJob(jobId),
    enabled: !!jobId,
  })
  const isConfigured = !!(config?.skills_rubrics && config.skills_rubrics.length > 0)

  const { data: sessionsData } = useQuery({
    queryKey: ['live-interview-sessions', jobId],
    queryFn: () => listSessionsForJob(jobId),
    enabled: !!jobId && !!isActive,
    refetchOnMount: 'always',
  })

  useEffect(() => {
    if (!sessionsData) return
    // Bulk data as the base; anything already resolved locally (a just-
    // created/updated session this mount) takes precedence over it.
    setSessions((prev) => ({ ...sessionsData, ...prev }))
  }, [sessionsData])

  // Three INDEPENDENT pending trackers. Join, Copy and Send are separate
  // actions — Join/Copy happen to hit the same idempotent get-or-create
  // endpoint, Send hits its own — but sharing one flag would make clicking
  // any one of them spin/disable the others.
  const [joiningJobMatchId, setJoiningJobMatchId] = useState<string | null>(null)
  const [copyingJobMatchId, setCopyingJobMatchId] = useState<string | null>(null)
  const [sendingJobMatchId, setSendingJobMatchId] = useState<string | null>(null)
  // Per-candidate Human/AI toggle, chosen right before "Send Email" (the
  // backend only honors a change while the session is still "scheduled" —
  // see send_live_interview_email). Defaults to whatever the session already
  // has once one exists, "human" otherwise.
  const [interviewModeByCandidate, setInterviewModeByCandidate] = useState<Record<string, 'human' | 'ai'>>({})

  const { data: candidateData, isLoading } = useQuery({
    queryKey: ['live-interview-candidates', jobId],
    queryFn: () => matchingApi.getComparisonResults(jobId).then((r) => r.data),
    enabled: !!jobId && !!isActive,
    refetchOnMount: 'always',
    // Keep checking while matching is still running, so the rest of the
    // candidates appear on their own as they finish.
    refetchInterval: (query: any) => {
      const s = query.state.data?.status
      return s === 'queued' || s === 'processing' ? 5000 : false
    },
  })

  // interviewMode is only actually SENT (and only actually applied
  // server-side, while the session is still "scheduled" — see
  // _maybe_set_interview_mode) when it differs from whatever mode the
  // existing session already has. Without this, picking "AI" in the
  // dropdown and then clicking Join Now/Copy Candidate URL on a candidate
  // who already had a session (created earlier, defaulting to "human")
  // would silently keep using "human" — resolveSession never even asks the
  // backend again once a session exists locally.
  const resolveSession = async (jobMatchId: string, interviewMode?: 'human' | 'ai'): Promise<LiveInterviewSession> => {
    const existing = sessions[jobMatchId]
    if (existing && (!interviewMode || existing.interview_mode === interviewMode)) return existing
    const session = await getOrCreateSession(jobMatchId, jobId, undefined, interviewMode)
    setSessions((prev) => ({ ...prev, [session.job_match_id]: session }))
    return session
  }

  const handleJoin = async (jobMatchId: string) => {
    setJoiningJobMatchId(jobMatchId)
    try {
      const mode = interviewModeByCandidate[jobMatchId] || sessions[jobMatchId]?.interview_mode || 'human'
      const session = await resolveSession(jobMatchId, mode)
      if (session.status === 'completed') {
        navigate(`/live-interview/sessions/${session.id}/summary`)
      } else {
        navigate(`/live-interview/sessions/${session.id}/room`)
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Failed to open the live interview room')
    } finally {
      setJoiningJobMatchId(null)
    }
  }

  const handleCopyCandidateUrl = async (jobMatchId: string) => {
    setCopyingJobMatchId(jobMatchId)
    try {
      const mode = interviewModeByCandidate[jobMatchId] || sessions[jobMatchId]?.interview_mode || 'human'
      const session = await resolveSession(jobMatchId, mode)
      const fullUrl = `${window.location.origin}${session.candidate_join_path}`
      try {
        await navigator.clipboard.writeText(fullUrl)
        toast.success('Candidate URL copied')
      } catch {
        toast.error('Could not copy the link — copy it manually')
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Failed to create the candidate link')
    } finally {
      setCopyingJobMatchId(null)
    }
  }

  // Emails the candidate their join link — mirrors ProctoredInterviewTab's
  // sendEmail handler. Unlike resolveSession (used by Join/Copy above), this
  // never needs to get-or-create the session itself first: the backend
  // endpoint does that server-side and hands back the (possibly
  // just-created) session, which is merged into local state exactly like
  // resolveSession's own setSessions call does.
  const handleSendEmail = async (jobMatchId: string, candidateEmail?: string) => {
    if (!candidateEmail) {
      toast.error('This candidate has no email on file.')
      return
    }
    const hadSentBefore = !!sessions[jobMatchId]?.invite_email_sent_at
    setSendingJobMatchId(jobMatchId)
    try {
      const mode = interviewModeByCandidate[jobMatchId] || sessions[jobMatchId]?.interview_mode || 'human'
      const result = await sendCandidateEmail(jobMatchId, mode)
      setSessions((prev) => ({ ...prev, [jobMatchId]: result.session }))
      toast.success(hadSentBefore ? 'Interview invitation re-sent' : (result.message || 'Interview invitation emailed'))
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Failed to send the email')
    } finally {
      setSendingJobMatchId(null)
    }
  }

  if (isLoading) {
    return <div className="flex justify-center py-12"><LoadingSpinner size="lg" /></div>
  }

  const status: string | undefined = candidateData?.status
  const candidates: CandidateRow[] = candidateData?.comparison_results || []

  return (
    <div className="space-y-6">
      <InterviewConfigEditor jobId={jobId} />

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <VideoCameraIcon className="w-4 h-4 text-gray-500" /> Candidates
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {isConfigured
              ? 'Join a candidate\'s live interview room, or click Send Email to email them their join link directly. The interview\'s first question is generated when you press "Start Interview" inside the room.'
              : 'Configure the interview skills and rubrics above before running interviews with candidates.'}
          </p>
        </div>

        {(status === 'queued' || status === 'processing') && candidates.length === 0 ? (
          <div className="p-12 text-center">
            <LoadingSpinner size="md" />
            <p className="text-sm text-gray-500 mt-4">
              Candidate matching is still processing — check back in a moment.
            </p>
          </div>
        ) : candidates.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-4">
              <VideoCameraIcon className="w-7 h-7 text-blue-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">No Matched Candidates Yet</h3>
            <p className="text-sm text-gray-500 mt-1.5 max-w-md mx-auto">
              Match candidates from the Apply Matching tab, then come back here to run a live interview
              with each one.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {(status === 'queued' || status === 'processing') && (
              <div className="px-6 py-2.5 bg-blue-50 text-xs text-blue-700 flex items-center gap-2">
                <LoadingSpinner size="sm" />
                More candidates are still being matched — they'll appear here once matching finishes.
              </div>
            )}
            {candidates.map((candidate) => {
              const session = sessions[candidate.id]
              const joining = joiningJobMatchId === candidate.id
              const copying = copyingJobMatchId === candidate.id
              const sending = sendingJobMatchId === candidate.id
              const initials = (candidate.candidate_name || '?').trim().split(/\s+/).slice(0, 2)
                .map((p) => p[0]?.toUpperCase()).join('') || '?'

              return (
                <div key={candidate.id} className="px-6 py-3.5 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold flex items-center justify-center flex-shrink-0">
                        {initials}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{candidate.candidate_name || 'Unnamed Candidate'}</p>
                        <p className="text-xs text-gray-500 truncate">{candidate.candidate_email}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 flex-shrink-0">
                      {session && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${statusBadgeClass(session.status)}`}>
                          {statusLabel(session.status)}
                        </span>
                      )}
                      {isConfigured ? (
                        <>
                          {/* Once completed, this button is a pure read (View
                              Scorecard) — available regardless of canManage.
                              Before that, clicking it creates/starts the
                              session (resolveSession's get-or-create), so
                              it's gated like every other write action below. */}
                          {(canManage || session?.status === 'completed') && (
                            <button
                              onClick={() => handleJoin(candidate.id)}
                              disabled={joining}
                              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium disabled:opacity-50 ${
                                session?.status === 'completed'
                                  ? 'bg-green-600 text-white hover:bg-green-700'
                                  : 'bg-blue-600 text-white hover:bg-blue-700'
                              }`}
                            >
                              {joining ? <LoadingSpinner size="sm" /> : session?.status === 'completed'
                                ? <DocumentTextIcon className="w-3.5 h-3.5" />
                                : <VideoCameraIcon className="w-3.5 h-3.5" />}
                              {joining ? 'Opening…' : session?.status === 'completed' ? 'View Scorecard' : 'Join Now'}
                            </button>
                          )}
                          {/* No point emailing/copying a join link once the
                              interview is done — the candidate's access token
                              still resolves, but there's nothing left for them
                              to do with it. Also gated on canManage: Copy
                              Candidate URL calls getOrCreateSession same as
                              Join Now (unlike ProctoredInterviewTab's Copy
                              Link, this isn't a pure clipboard read), and the
                              mode select + Send Email both write too. */}
                          {canManage && session?.status !== 'completed' && (
                            <>
                              {session?.invite_email_sent_at && (
                                <span
                                  title={`Last sent ${new Date(session.invite_email_sent_at).toLocaleString()}`}
                                  className="inline-flex items-center gap-1 text-[11px] text-green-700"
                                >
                                  <CheckCircleIcon className="w-3.5 h-3.5" /> Sent
                                </span>
                              )}
                              {/* Human/AI interviewer toggle — only meaningful before the
                                  candidate has actually joined (the backend ignores a change
                                  once the session leaves "scheduled"), so it's fine to leave
                                  this editable right up until the invite is (re)sent. */}
                              {(() => {
                                const currentMode = interviewModeByCandidate[candidate.id] || session?.interview_mode || 'human'
                                const isAi = currentMode === 'ai'
                                return (
                              <button
                                type="button"
                                role="switch"
                                aria-checked={isAi}
                                onClick={() => {
                                  const mode: 'human' | 'ai' = isAi ? 'human' : 'ai'
                                  setInterviewModeByCandidate((prev) => ({ ...prev, [candidate.id]: mode }))
                                  // Persisted immediately on selection, not deferred until Send
                                  // Email/Join Now/Copy Candidate URL is next clicked — otherwise
                                  // picking "AI" and simply reloading the page (with no action
                                  // button click in between) looked like it "didn't save",
                                  // because nothing had actually been sent to the backend yet.
                                  resolveSession(candidate.id, mode).catch(() => {
                                    toast.error('Could not save the interview mode — please try again.')
                                  })
                                }}
                                title="Who conducts this interview: a recruiter (Human), or the AI interviewer (AI)"
                                className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-700 whitespace-nowrap"
                              >
                                <span
                                  className={`relative inline-flex h-4 w-7 flex-shrink-0 items-center rounded-full transition-colors ${
                                    isAi ? 'bg-indigo-600' : 'bg-gray-300'
                                  }`}
                                >
                                  <span
                                    className={`inline-block h-3 w-3 rounded-full bg-white shadow transition-transform ${
                                      isAi ? 'translate-x-3.5' : 'translate-x-0.5'
                                    }`}
                                  />
                                </span>
                                <span className="w-9 text-left">{isAi ? 'AI' : 'Human'}</span>
                              </button>
                                )
                              })()}
                              <button
                                onClick={() => handleSendEmail(candidate.id, candidate.candidate_email)}
                                disabled={sending}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-md text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                              >
                                {sending ? <LoadingSpinner size="sm" /> : <EnvelopeIcon className="w-3.5 h-3.5" />}
                                {sending ? 'Sending…' : session?.invite_email_sent_at ? 'Resend Email' : 'Send Email'}
                              </button>
                              <button
                                onClick={() => handleCopyCandidateUrl(candidate.id)}
                                disabled={copying}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-md text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                              >
                                {copying ? <LoadingSpinner size="sm" /> : <ClipboardDocumentIcon className="w-3.5 h-3.5" />}
                                {copying ? 'Preparing…' : 'Copy Candidate URL'}
                              </button>
                            </>
                          )}
                        </>
                      ) : (
                        <span className="text-xs text-gray-400 italic">Configure interview above to enable</span>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// Interview configuration — the shared, per-job LiveInterviewConfig.
// Structured editor (skill name / rubric criteria rows / question-type ×
// count grid), same shape ProctoredInterviewTab's ConfigWizard uses.
// ============================================================================

function emptySkill(name: string): SkillRubric {
  return { skill_name: name, weight: 1, rubric_criteria: [], question_mix: [] }
}

// Visual identity per question-type category, so the "Question Mix" grid
// reads at a glance instead of as an undifferentiated list of names — a
// recruiter scanning several skill cards can tell "this one has a coding
// question" from the icon/color alone. Falls back to a neutral chat icon for
// any category an org has customized away from the coding/design/verbal
// defaults (category is a free-text display grouping, not an enum — see
// database/models.py QuestionType.category).
const CATEGORY_META: Record<string, { icon: typeof CodeBracketIcon; classes: string }> = {
  coding: { icon: CodeBracketIcon, classes: 'bg-violet-50 text-violet-700 border-violet-200' },
  design: { icon: Square3Stack3DIcon, classes: 'bg-amber-50 text-amber-700 border-amber-200' },
  verbal: { icon: MicrophoneIcon, classes: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
}

function categoryMeta(category?: string) {
  return CATEGORY_META[(category || '').toLowerCase()] || { icon: DocumentTextIcon, classes: 'bg-gray-50 text-gray-600 border-gray-200' }
}

function normalizeSkillWords(name: string): string[] {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, ' ').split(' ').filter(Boolean)
}

// Two skill names came from two DIFFERENT extraction runs (an old, already-
// configured skill vs. a fresh jd_context) and often differ slightly in
// phrasing even when they mean the same skill — e.g. "SQL (Oracle SQL,
// sqlplus)" vs "SQL (Oracle/sqlplus)", or "Python Scripting & Automation" vs
// "Python Scripting". An exact match misses all of these. This checks
// whether at least 70% of the SHORTER name's words appear in the other —
// verified against six real skill-name pairs from an actual job (all
// matched correctly) without producing false positives between genuinely
// different skills (near-zero word overlap).
function skillNamesMatch(a: string, b: string): boolean {
  const wa = normalizeSkillWords(a)
  const wb = normalizeSkillWords(b)
  if (wa.length === 0 || wb.length === 0) return false
  if (wa.join(' ') === wb.join(' ')) return true
  const [shorter, longer] = wa.length <= wb.length ? [wa, wb] : [wb, wa]
  const longerSet = new Set(longer)
  const overlap = shorter.filter((w) => longerSet.has(w)).length
  return overlap / shorter.length >= 0.7
}

// ============================================================================
// History (audit log) modal — who created/edited this config, and when.
// Mirrors ProctoredInterviewTab's HistoryModal/auditDetailLine exactly,
// narrowed to Live Interview's own action vocabulary (no question-bank
// regeneration or per-question edit actions here — see
// LiveInterviewConfigAuditLog in backend/database/models.py).
// ============================================================================

const AUDIT_ACTION_LABELS: Record<string, string> = {
  created: 'Created the interview configuration',
  context_extracted: 'Extracted context from the job description',
  skills_updated: 'Updated skills & rubrics',
}

function auditDetailLine(entry: AuditLogEntry): string | null {
  const d = entry.details || {}
  switch (entry.action) {
    case 'skills_updated':
      return Array.isArray(d.skills) ? d.skills.join(', ') : null
    case 'context_extracted':
      return d.role_title || null
    default:
      return null
  }
}

function HistoryModal({ configId, onClose }: { configId: string; onClose: () => void }) {
  const { data: entries, isLoading } = useQuery({
    queryKey: ['live-interview-config-audit-log', configId],
    queryFn: () => getAuditLog(configId),
  })

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[80vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Interview History</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="w-5 h-5" /></button>
        </div>
        {isLoading ? (
          <div className="flex justify-center py-8"><LoadingSpinner size="md" /></div>
        ) : !entries || entries.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8">No history yet.</p>
        ) : (
          <div className="space-y-3">
            {entries.map((entry, idx) => {
              const detail = auditDetailLine(entry)
              return (
                <div key={idx} className="flex gap-3 text-sm">
                  <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded font-mono flex-shrink-0 mt-0.5">v{entry.version}</span>
                  <div className="min-w-0">
                    <p className="text-gray-900">{AUDIT_ACTION_LABELS[entry.action] || entry.action}</p>
                    {detail && <p className="text-xs text-gray-500 truncate">{detail}</p>}
                    <p className="text-xs text-gray-400">
                      {entry.changed_by_name || 'Unknown user'} · {new Date(entry.created_at).toLocaleString(undefined, {
                        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                      })}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

function InterviewConfigEditor({ jobId }: { jobId: string }) {
  const queryClient = useQueryClient()
  // Added 2026-10-07 — gates Create/Extract/Save/Edit the same way
  // ProctoredInterviewTab's ConfigWizard does. Computed independently here
  // rather than threaded from LiveInterviewTab, matching how every other
  // capability check in this codebase is a local usePermission() call.
  const { can } = usePermission()
  const canManage = can('liveInterview.manage')
  const [config, setConfig] = useState<LiveInterviewConfig | null>(null)
  const [skills, setSkills] = useState<SkillRubric[]>([])
  const [newSkillName, setNewSkillName] = useState('')
  // Shown once, right after an extraction actually seeds question counts (see
  // extractMutation below) — extraction is a no-op on skills_rubrics once any
  // exist (backend: "if not config.skills_rubrics"), so this only ever fires
  // on a genuinely fresh config, which is exactly when a recruiter needs the
  // "these numbers came from the AI, not from you" context.
  const [justExtractedMix, setJustExtractedMix] = useState(false)
  // Mirrors Proctored Interview's collapsed "Configured" summary + "Edit"
  // pattern (see ProctoredInterviewTab) instead of always showing the full
  // Job Context / Skills editor — a recruiter who already set this job's
  // interview up shouldn't have to scroll past two full steps every time
  // they open this tab just to glance at what's configured. Decided once,
  // the first time the config loads (see hasInitializedEditingRef below):
  // a brand-new job with no skills yet opens straight into the editor; an
  // already-configured job opens collapsed.
  const [isEditing, setIsEditing] = useState(false)
  const hasInitializedEditingRef = useRef(false)
  // History (audit log) modal — mirrors ProctoredInterviewTab's showHistory.
  const [showHistory, setShowHistory] = useState(false)
  // Whether the "add a skill" combobox's suggestion dropdown is open — see
  // extractedSkillSuggestions below.
  const [showSkillSuggestions, setShowSkillSuggestions] = useState(false)

  const jdContext = config?.jd_context || {}

  const { data: questionTypes } = useQuery({
    queryKey: ['question-types-active'],
    queryFn: () => fetchQuestionTypes(false),
  })

  const { data: loadedConfig, isLoading: configLoading } = useQuery({
    queryKey: ['live-interview-config', jobId],
    queryFn: () => getConfigForJob(jobId),
    enabled: !!jobId,
    refetchOnMount: 'always',
  })

  // Hydrate local edit state from whatever the server currently holds. Keyed
  // on the config's identity+created_at rather than on every render so an
  // in-progress edit isn't stomped by a background refetch.
  useEffect(() => {
    if (loadedConfig === undefined) return
    setConfig(loadedConfig)
    const candidateSkills = Array.isArray(loadedConfig?.jd_context?.candidate_skills)
      ? loadedConfig.jd_context.candidate_skills
      : []
    const nextSkills = (loadedConfig?.skills_rubrics || []).map((s) => {
      let question_mix = s.question_mix || []
      // Auto-map: a skill with no question_mix yet, whose name fuzzy-matches
      // a jd_context suggestion (see skillNamesMatch — handles rephrasing
      // between extraction runs, not just identical names), gets that
      // suggestion's counts applied automatically — no manual "apply
      // suggestion" step. Only fires when question_mix is currently empty,
      // so it can never overwrite a recruiter's own edits.
      if (question_mix.length === 0) {
        const match = candidateSkills.find(
          (c: any) => c?.skill_name && skillNamesMatch(c.skill_name, s.skill_name)
        )
        if (match) question_mix = resolveSuggestedQuestionMix(match.suggested_question_mix)
      }
      return { ...s, rubric_criteria: s.rubric_criteria || [], question_mix }
    })
    setSkills(nextSkills)
    if (!hasInitializedEditingRef.current) {
      hasInitializedEditingRef.current = true
      // canManage-gated — a view-only role must never land in the editor
      // automatically, even for a brand-new job with no skills set up yet.
      setIsEditing(canManage && nextSkills.length === 0)
    }
  }, [loadedConfig])

  const extractMutation = useMutation({
    mutationFn: () => extractJdContext(jobId),
    onSuccess: (cfg) => {
      setConfig(cfg)
      queryClient.setQueryData(['live-interview-config', jobId], cfg)
      // The backend seeds skills/rubrics from the AI's suggested candidate
      // skills only when none were configured yet, so this never clobbers an
      // in-progress edit — reflect whatever came back.
      const nextSkills = (cfg.skills_rubrics || []).map((s) => ({
        ...s,
        rubric_criteria: s.rubric_criteria || [],
        question_mix: s.question_mix || [],
      }))
      setSkills(nextSkills)
      const seededQuestions = nextSkills.some((s) => s.question_mix.length > 0)
      setJustExtractedMix(seededQuestions)
      toast.success(
        seededQuestions
          ? 'Job context extracted — skills, rubrics and suggested question counts are pre-filled below'
          : 'Job context extracted'
      )
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Failed to extract job context'),
  })

  // Every skill must ask at least one question. Same rule (and reason) as the
  // Proctored editor: an empty question mix gets saved, and on the next load
  // the auto-map above refills EMPTY mixes from the JD suggestion — which
  // looks like the old values coming back.
  const skillsWithoutQuestions = skills
    .filter((s) => !(s.question_mix || []).some((e) => e.count > 0))
    .map((s) => s.skill_name)
  const [showMixErrors, setShowMixErrors] = useState(false)
  const mixIsValid = () => {
    if (skillsWithoutQuestions.length === 0) return true
    setShowMixErrors(true)
    toast.error(
      skillsWithoutQuestions.length === 1
        ? `"${skillsWithoutQuestions[0]}" has no questions. Add at least one, or remove the skill.`
        : `${skillsWithoutQuestions.length} skills have no questions. Add at least one to each, or remove them.`,
    )
    return false
  }

  const saveMutation = useMutation({
    mutationFn: () => saveSkillsRubricsForJob(jobId, skills),
    onSuccess: (cfg) => {
      setConfig(cfg)
      // Share the saved config with the rest of the tab — the candidates list
      // below reads ['live-interview-config', jobId] to decide whether
      // interviews can run, so without this it stayed hidden until a reload.
      queryClient.setQueryData(['live-interview-config', jobId], cfg)
      queryClient.invalidateQueries({ queryKey: ['live-interview-candidates', jobId] })
      queryClient.invalidateQueries({ queryKey: ['live-interview-sessions', jobId] })
      setIsEditing(false)
      toast.success('Interview configuration saved')
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Failed to save the configuration'),
  })

  const addSkill = () => {
    const name = newSkillName.trim()
    if (!name) return
    if (skills.length >= MAX_SKILLS) {
      toast.error(`A single interview cannot assess more than ${MAX_SKILLS} skills.`)
      return
    }
    if (skills.some((s) => s.skill_name.toLowerCase() === name.toLowerCase())) {
      toast.error('That skill is already in the list.')
      return
    }
    // Falls back to a suggestion with a matching name (if the recruiter typed
    // an extracted skill's name out by hand instead of clicking it in the
    // dropdown) so it still arrives with its real rubric/question-mix
    // suggestions rather than starting completely blank.
    const matchingSuggestion = extractedSkillSuggestions.find(
      (c) => (c.skill_name || '').trim().toLowerCase() === name.toLowerCase()
    )
    if (matchingSuggestion) {
      addSkillFromSuggestion(matchingSuggestion)
      return
    }
    setSkills([...skills, emptySkill(name)])
    setNewSkillName('')
  }

  // Resolves a jd_context candidate_skill's suggested_question_mix (type
  // NAMES only, per question_generation.extract_jd_context) against the
  // org's real active question types — same name-matching principle the
  // backend seeding uses, so a skill re-added from suggestions here behaves
  // identically to one seeded straight from a fresh extraction.
  const resolveSuggestedQuestionMix = (suggested: any[] | undefined): QuestionMixEntry[] => {
    const mix: QuestionMixEntry[] = []
    for (const entry of suggested || []) {
      const wantedName = (entry?.question_type_name || '').trim().toLowerCase()
      if (!wantedName) continue
      const type = (questionTypes || []).find((t) => t.name.trim().toLowerCase() === wantedName)
      if (!type) continue
      const count = Math.max(0, Math.min(20, Number(entry?.count) || 0))
      if (count <= 0) continue
      mix.push({ question_type_id: type.id, question_type_name: type.name, response_mode: type.response_mode, category: type.category, count })
    }
    return mix
  }

  // Every skill the last JD extraction identified (up to 12 — see
  // question_generation.py), minus whichever ones are already in the
  // interview. Only the first 6 (by importance) get auto-seeded when a
  // config is first extracted; the rest surface here so removing/re-adding
  // a skill, or picking one the auto-seed didn't include, still restores its
  // real AI-suggested rubric and question mix instead of starting blank.
  const extractedSkillSuggestions: any[] = Array.isArray(jdContext?.candidate_skills)
    ? jdContext.candidate_skills.filter((c: any) =>
        c?.skill_name && !skills.some((s) => s.skill_name.toLowerCase() === c.skill_name.trim().toLowerCase())
      )
    : []

  const filteredSuggestions = extractedSkillSuggestions.filter((c) =>
    !newSkillName.trim() || c.skill_name.toLowerCase().includes(newSkillName.trim().toLowerCase())
  )

  const addSkillFromSuggestion = (candidate: any) => {
    const name = (candidate?.skill_name || '').trim()
    if (!name) return
    if (skills.length >= MAX_SKILLS) {
      toast.error(`A single interview cannot assess more than ${MAX_SKILLS} skills.`)
      return
    }
    if (skills.some((s) => s.skill_name.toLowerCase() === name.toLowerCase())) return
    const rubric_criteria = (candidate.suggested_rubric || [])
      .filter((c: any) => c && c.name)
      .map((c: any) => ({ name: c.name, description: c.description }))
    setSkills([...skills, {
      skill_name: name,
      weight: 1,
      rubric_criteria,
      question_mix: resolveSuggestedQuestionMix(candidate.suggested_question_mix),
    }])
    setNewSkillName('')
    setShowSkillSuggestions(false)
  }

  const removeSkill = (skillName: string) => setSkills(skills.filter((s) => s.skill_name !== skillName))

  // Overwrites one existing skill's question_mix from a chosen jd_context
  // suggestion — the in-place counterpart to addSkillFromSuggestion, for a
  // skill that's already on the list (e.g. seeded before this feature
  // existed, or typed in by hand) rather than one being newly added.
  const applySuggestedMixToSkill = (skillName: string, candidateSkillName: string) => {
    const candidate = (Array.isArray(jdContext?.candidate_skills) ? jdContext.candidate_skills : [])
      .find((c: any) => c?.skill_name === candidateSkillName)
    if (!candidate) return
    const mix = resolveSuggestedQuestionMix(candidate.suggested_question_mix)
    if (mix.length === 0) {
      toast.error(`"${candidateSkillName}" has no suggested question types that match this org's active types.`)
      return
    }
    setSkills(skills.map((s) => (s.skill_name === skillName ? { ...s, question_mix: mix } : s)))
    toast.success(`Applied suggested question mix from "${candidateSkillName}"`)
  }

  const addCriterion = (skillName: string) => {
    setSkills(skills.map((s) => s.skill_name === skillName
      ? { ...s, rubric_criteria: [...s.rubric_criteria, { name: '', description: '' }] }
      : s))
  }

  const updateCriterion = (skillName: string, idx: number, field: 'name' | 'description', value: string) => {
    setSkills(skills.map((s) => s.skill_name === skillName
      ? { ...s, rubric_criteria: s.rubric_criteria.map((c, i) => (i === idx ? { ...c, [field]: value } : c)) }
      : s))
  }

  const removeCriterion = (skillName: string, idx: number) => {
    setSkills(skills.map((s) => s.skill_name === skillName
      ? { ...s, rubric_criteria: s.rubric_criteria.filter((_, i) => i !== idx) }
      : s))
  }

  // The session's question quota is built verbatim from these entries (see
  // session_service._build_quota), and the candidate room picks its answer
  // UI from the entry's response_mode/category — so denormalize the question
  // type's name/mode/category into the entry here rather than leaving the
  // room to look them up from a reference list it doesn't load.
  const setMixCount = (skillName: string, type: QuestionType, count: number) => {
    setSkills(skills.map((s) => {
      if (s.skill_name !== skillName) return s
      const matches = (e: QuestionMixEntry) => e.question_type_id === type.id
      if (count <= 0) return { ...s, question_mix: s.question_mix.filter((e) => !matches(e)) }
      const entry: QuestionMixEntry = {
        question_type_id: type.id,
        question_type_name: type.name,
        response_mode: type.response_mode,
        category: type.category,
        count,
      }
      return {
        ...s,
        question_mix: s.question_mix.some(matches)
          ? s.question_mix.map((e) => (matches(e) ? { ...e, ...entry } : e))
          : [...s.question_mix, entry],
      }
    }))
  }

  const getMixCount = (skill: SkillRubric, typeId: string) =>
    skill.question_mix.find((e) => e.question_type_id === typeId)?.count || 0

  const totalQuestions = skills.reduce(
    (sum, s) => sum + s.question_mix.reduce((n, e) => n + (e.count || 0), 0), 0
  )
  const hasJdContext = Object.keys(jdContext).length > 0

  if (configLoading) {
    return <div className="flex justify-center py-12"><LoadingSpinner size="lg" /></div>
  }

  // ------------------------------------------------------------------
  // No config yet — same empty state as ProctoredInterviewTab's, and
  // "Create Live Interview" triggers the same extract mutation the
  // "Re-extract from JD" button below uses: extractJdContext already
  // get-or-creates the config AND runs extraction in one backend call, so
  // creating and auto-extracting happens together here too.
  // ------------------------------------------------------------------
  if (!config) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
        <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-4">
          <SparklesIcon className="w-7 h-7 text-blue-500" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">No Live Interview Yet</h3>
        <p className="text-sm text-gray-500 mt-1.5 mb-5 max-w-md mx-auto">
          {canManage
            ? 'Create one to extract context from this job description, then choose the skills, rubrics and question mix every candidate\'s live interview draws from.'
            : 'No Live Interview has been set up for this job yet.'}
        </p>
        {canManage && (
          <button
            onClick={() => extractMutation.mutate()}
            disabled={extractMutation.isPending}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 shadow-sm"
          >
            {extractMutation.isPending ? <LoadingSpinner size="sm" /> : <PlusIcon className="w-4 h-4" />}
            {extractMutation.isPending ? 'Creating…' : 'Create Live Interview'}
          </button>
        )}
      </div>
    )
  }

  return (
    <>
    {/* No overflow-hidden here (unlike most cards): it clipped the "add a
        skill" suggestion dropdown, which opens below the card's last row.
        The header rounds its own top corners instead. */}
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="px-5 py-3 border-b border-gray-100 bg-gray-50 rounded-t-lg flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <SparklesIcon className="w-4 h-4 text-gray-500" /> Interview Configuration
        </h3>
        <InfoTooltip
          align="right"
          text="Shared by every candidate's live interview for this job — extract context from the job description, then set the skills, rubric criteria and question mix each interview draws from."
        />
      </div>

      {!isEditing && skills.length > 0 ? (
        <div className="p-5">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-7 h-7 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0">
                <CheckCircleIcon className="w-4 h-4 text-green-600" />
              </div>
              <div className="min-w-0">
                <h4 className="text-sm font-semibold text-gray-900">Live Interview Configured</h4>
                <p className="text-xs text-gray-500 mt-0.5">
                  {skills.length} skill{skills.length === 1 ? '' : 's'} · {totalQuestions} question{totalQuestions === 1 ? '' : 's'} per interview
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {canManage && (
                <InfoTooltip text="This configuration is shared by every candidate's live interview for this job — editing it changes what future questions are generated, including for interviews already in progress." />
              )}
              <button
                onClick={() => setShowHistory(true)}
                title="Audit"
                aria-label="Audit"
                className="inline-flex items-center justify-center w-9 h-9 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                <ClipboardDocumentCheckIcon className="w-5 h-5" />
              </button>
              {canManage && (
                <button
                  onClick={() => setIsEditing(true)}
                  title="Edit / Regenerate"
                  aria-label="Edit / Regenerate"
                  className="inline-flex items-center justify-center w-9 h-9 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  <span className="relative inline-flex items-center justify-center w-5 h-5">
                    <PencilSquareIcon className="w-5 h-5" />
                    <ArrowPathIcon className="w-3 h-3 absolute -bottom-1 -right-1 bg-white rounded-full text-blue-600" />
                  </span>
                </button>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-3">
            {skills.map((s) => (
              <span key={s.skill_name} className="text-[11px] font-medium px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full">{s.skill_name}</span>
            ))}
          </div>
        </div>
      ) : canManage ? (
        <div className="p-5 space-y-5">
          {/* Sticky top bar — mirrors ProctoredInterviewTab's ConfigWizard:
              Re-extract on the left, Save on the right, pinned to the top of
              the viewport once scrolled that far. No "Generate Question
              Bank" equivalent here — Live Interview never pre-generates a
              question bank, questions are generated live during the call. */}
          <div className="sticky top-0 z-20 flex items-center justify-between gap-3 -mx-5 -mt-5 px-5 py-3 border-b border-gray-200 bg-gray-50/95 backdrop-blur-sm shadow-sm rounded-t-lg">
            <button
              onClick={() => extractMutation.mutate()}
              disabled={extractMutation.isPending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-100 disabled:opacity-50"
            >
              {extractMutation.isPending ? <LoadingSpinner size="sm" /> : <SparklesIcon className="w-3.5 h-3.5" />}
              {extractMutation.isPending ? 'Extracting…' : hasJdContext ? 'Re-extract from JD' : 'Extract Context from JD'}
            </button>
            <button
              onClick={() => { if (mixIsValid()) saveMutation.mutate() }}
              disabled={saveMutation.isPending}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 shadow-sm"
            >
              {saveMutation.isPending && <LoadingSpinner size="sm" />}
              {saveMutation.isPending ? 'Saving…' : 'Save Configuration'}
            </button>
          </div>

          {skills.length > 0 && (
            <button onClick={() => setIsEditing(false)} className="text-xs text-gray-500 hover:text-gray-700">
              ← Back to summary
            </button>
          )}
          {/* Step 1 — job context */}
          <div>
            <div className="flex items-center justify-between mb-2.5">
              <h4 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0">1</span>
                Job Context
              </h4>
            </div>
            {extractMutation.isPending ? (
              <div className="text-xs text-gray-500 bg-gray-50 rounded-lg p-3 flex items-center gap-2 ml-7">
                <LoadingSpinner size="sm" /> Reading the job description and identifying key skills…
              </div>
            ) : hasJdContext ? (
              <div className="text-xs text-gray-600 bg-gray-50 rounded-lg p-3 space-y-1 ml-7">
                <p><span className="font-medium">Role:</span> {jdContext.role_title} ({jdContext.seniority_level})</p>
                {jdContext.summary && <p>{jdContext.summary}</p>}
              </div>
            ) : (
              <p className="text-xs text-gray-400 ml-7">
                Not extracted yet — this pre-fills the skills and rubric suggestions below.
              </p>
            )}
          </div>

          {/* Step 2 — skills, rubrics & question mix */}
          <div>
            <h4 className="flex items-center gap-2.5 text-sm font-semibold text-gray-900 mb-3">
              <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">2</span>
              Skills, Rubrics &amp; Question Mix
              <span className="text-xs font-normal text-gray-400">
                ({skills.length}/{MAX_SKILLS} skills · {totalQuestions} question{totalQuestions === 1 ? '' : 's'})
              </span>
            </h4>

            {justExtractedMix && (
              <div className="lg:ml-8 mb-4 flex items-start gap-2 bg-indigo-50 border border-indigo-100 rounded-lg px-3.5 py-2.5">
                <SparklesIcon className="w-4 h-4 text-indigo-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-indigo-800 flex-1">
                  Question counts below are AI-suggested from the job description — each skill only got counts
                  for question types that make sense for it (e.g. no coding questions for a purely verbal skill).
                  Review and adjust before saving.
                </p>
                <button onClick={() => setJustExtractedMix(false)} className="text-indigo-400 hover:text-indigo-600 flex-shrink-0">
                  <XMarkIcon className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            <div className="space-y-4 lg:ml-8">
              {skills.length === 0 && (
                <p className="text-xs text-gray-400">
                  No skills configured yet — extract the job context above to get suggestions, or add one below.
                </p>
              )}
              {skills.map((skill) => {
                const skillTotal = skill.question_mix.reduce((n, e) => n + (e.count || 0), 0)
                return (
                  <div key={skill.skill_name} className="border border-gray-200 rounded-lg overflow-hidden hover:border-blue-200 transition-colors">
                    <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100">
                      <div className="flex items-center gap-2.5">
                        <span className="text-sm font-semibold text-gray-900">{skill.skill_name}</span>
                        <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${
                          skillTotal > 0 ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-400'
                        }`}>
                          {skillTotal} question{skillTotal === 1 ? '' : 's'}
                        </span>
                      </div>
                      <button onClick={() => removeSkill(skill.skill_name)} className="text-gray-400 hover:text-red-500">
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="p-4 space-y-4">
                      {/* Assessment rubric — full width so criterion rows stay legible instead of
                          squeezed into a half-width column, and so this section's row count never
                          visually implies any relationship to the question-mix chips below it. */}
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Assessment Rubric</p>
                        <div className="space-y-1.5">
                          {skill.rubric_criteria.map((c, idx) => (
                            <div key={idx} className="flex gap-2 items-start">
                              <input
                                value={c.name}
                                onChange={(e) => updateCriterion(skill.skill_name, idx, 'name', e.target.value)}
                                placeholder="Criterion name"
                                className="flex-1 min-w-0 border border-gray-200 rounded-md px-2.5 py-1.5 text-xs focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                              />
                              <input
                                value={c.description || ''}
                                onChange={(e) => updateCriterion(skill.skill_name, idx, 'description', e.target.value)}
                                placeholder="What a strong answer shows"
                                className="flex-1 min-w-0 border border-gray-200 rounded-md px-2.5 py-1.5 text-xs focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                              />
                              <button onClick={() => removeCriterion(skill.skill_name, idx)} className="text-gray-300 hover:text-red-500 mt-1.5">
                                <TrashIcon className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                        <button
                          onClick={() => addCriterion(skill.skill_name)}
                          className="mt-2 text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        >
                          <PlusIcon className="w-3 h-3" /> Add rubric criterion
                        </button>
                      </div>

                      {/* Question mix — one chip per question type, grouped visually by
                          category icon/color so "which types does this skill actually use"
                          reads at a glance across several skill cards. */}
                      <div className="pt-3.5 border-t border-gray-100">
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Question Mix</p>
                          {/* An older config (created before AI-suggested counts existed, or a
                              skill added by hand) can have a real rubric but an empty mix with
                              no way to get one short of deleting and re-adding via the "Add a
                              skill" suggestions box. This lets that happen in place, without
                              losing the skill's position, rubric edits, or name. Always offered
                              (not just when empty) since a recruiter may also want to reset a
                              mix back to the AI's original suggestion after fiddling with it. */}
                          {jdContext?.candidate_skills?.length > 0 && (
                            <select
                              value=""
                              onChange={(e) => { if (e.target.value) applySuggestedMixToSkill(skill.skill_name, e.target.value) }}
                              className="text-[11px] border border-gray-200 rounded-md px-1.5 py-1 text-gray-500 bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 max-w-[140px]"
                            >
                              <option value="">✨ Apply suggestion…</option>
                              {jdContext.candidate_skills.map((c: any) => (
                                <option key={c.skill_name} value={c.skill_name}>{c.skill_name}</option>
                              ))}
                            </select>
                          )}
                        </div>
                        {showMixErrors && skillsWithoutQuestions.includes(skill.skill_name) && (
                          <p role="alert" className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-md px-3 py-2 mb-2">
                            Add at least 1 question (Coding, Design or Verbal) for this skill, or remove the skill.
                          </p>
                        )}
                        {(questionTypes || []).length === 0 ? (
                          <p className="text-xs text-gray-400">No question types configured for this organization yet.</p>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {(questionTypes || []).map((type) => {
                              const count = getMixCount(skill, type.id)
                              const meta = categoryMeta(type.category)
                              const Icon = meta.icon
                              return (
                                <div
                                  key={type.id}
                                  className={`flex items-center gap-2.5 rounded-lg border px-2.5 py-2 ${
                                    count > 0 ? meta.classes : 'bg-white border-gray-200'
                                  }`}
                                  title={`Response mode: ${type.response_mode}`}
                                >
                                  <Icon className={`w-4 h-4 flex-shrink-0 ${count > 0 ? '' : 'text-gray-400'}`} />
                                  <div className="min-w-0 leading-tight">
                                    <p className={`text-xs font-medium truncate ${count > 0 ? '' : 'text-gray-500'}`}>{type.name}</p>
                                    <p className="text-[10px] opacity-70 truncate">{type.response_mode}</p>
                                  </div>
                                  <div className="flex items-center gap-1.5 flex-shrink-0 ml-1">
                                    <button
                                      onClick={() => setMixCount(skill.skill_name, type, Math.max(0, count - 1))}
                                      disabled={count <= 0}
                                      className="w-5 h-5 flex items-center justify-center rounded bg-black/5 hover:bg-black/10 disabled:opacity-30"
                                    >
                                      <MinusIcon className="w-3 h-3" />
                                    </button>
                                    {/* A plain, always-rendered number — not an <input> — so the count
                                        can never be silently invisible behind any browser/controlled-
                                        input quirk. Click-to-type isn't needed at this range (0-20);
                                        the +/- buttons are the only input method. */}
                                    <span className="w-4 text-center text-sm font-bold text-gray-900 tabular-nums select-none">
                                      {count}
                                    </span>
                                    <button
                                      onClick={() => setMixCount(skill.skill_name, type, Math.min(20, count + 1))}
                                      className="w-5 h-5 flex items-center justify-center rounded bg-black/5 hover:bg-black/10"
                                    >
                                      <PlusIcon className="w-3 h-3" />
                                    </button>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            {skills.length < MAX_SKILLS && (
              <div className="relative mt-3 lg:ml-8">
                <div className="flex gap-2">
                  <input
                    value={newSkillName}
                    onChange={(e) => setNewSkillName(e.target.value)}
                    onFocus={() => setShowSkillSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSkillSuggestions(false), 150)}
                    onKeyDown={(e) => e.key === 'Enter' && addSkill()}
                    placeholder={
                      extractedSkillSuggestions.length > 0
                        ? 'Type a skill, or pick one extracted from the job description below'
                        : 'Add a skill to assess'
                    }
                    className="flex-1 min-w-0 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                  <button onClick={addSkill} className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 flex-shrink-0">
                    Add
                  </button>
                </div>

                {showSkillSuggestions && filteredSuggestions.length > 0 && (
                  <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                    <p className="px-3 pt-2 pb-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                      From the job description
                    </p>
                    {filteredSuggestions.map((c: any) => (
                      <button
                        key={c.skill_name}
                        // onMouseDown (not onClick) fires before the input's onBlur closes
                        // this dropdown, so the click actually lands on the suggestion.
                        onMouseDown={(e) => { e.preventDefault(); addSkillFromSuggestion(c) }}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 flex items-center justify-between gap-2"
                      >
                        <span className="truncate">{c.skill_name}</span>
                        <PlusIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {totalQuestions === 0 && skills.length > 0 && (
            <p className="text-xs text-amber-600 pt-4 border-t border-gray-100">
              Set at least one question count above — an interview with no questions cannot be started.
            </p>
          )}
        </div>
      ) : (
        // canManage is false here, and skills.length === 0 (the only other
        // way to reach this branch) — a view-only role looking at a config
        // nobody has finished setting up yet. Without this, they'd fall
        // through to the full editor above purely because skills is empty.
        <div className="p-5 text-center">
          <p className="text-sm text-gray-500">Setup for this Live Interview is still in progress.</p>
        </div>
      )}
    </div>
    {showHistory && config && (
      <HistoryModal configId={config.id} onClose={() => setShowHistory(false)} />
    )}
    </>
  )
}
